import React, { ReactNode, useContext, useRef } from "react";
import { Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DraggableProvided, DraggableStateSnapshot } from "react-beautiful-dnd";
import cn from "classnames";

import { UpdatePageModel } from "../UpdatePageModel";
import { TargetCounts, ThreeDotMenu } from 'components';
import { FilterType } from "enums";
import { IFilter } from "interfaces";
import { formatDateAndTime, pluralize } from "services/Utils";
import { BoardContext } from "pages";
import { useTranslation } from "react-i18next";
import { useLuxonTimezone } from "hooks";
import { UsersService } from "services";
import { FilterMenu } from "./FilterMenu";

interface IFilterCardProps {
    canEdit: boolean,
    dragProvided: DraggableProvided,
    dragSnapshot: DraggableStateSnapshot,
    filter: IFilter,
    isGlobal: boolean,
    model: UpdatePageModel,
    stageId: number,
    stageIndex: number,

    onEditFilter: (filter: IFilter, isNew: boolean) => void,
}

export function FilterCard({ canEdit, dragProvided, dragSnapshot, filter, isGlobal, model, stageId, stageIndex, onEditFilter }: IFilterCardProps) {
    const menuRef = useRef<HTMLDivElement>();
    const boardContext = useContext(BoardContext);
    const { t } = useTranslation();
    const { timezone, hasTimezone } = useLuxonTimezone(UsersService.me.timezone);

    let filterBody: ReactNode;

    function onClick(e: React.MouseEvent) {
        // Ignore clicks from our menu button
        if (menuRef.current && menuRef.current.contains(e.target as Node)) {
            return;
        }
        onEditFilter(filter, false)
    }

    function onDuplicate() {
        model.duplicateFilter({
            filterId: filter.id,
            isGlobal: isGlobal,
            stageId: stageId,
        }).catch(() => boardContext.toastError(model.apiCallError));
    }

    function onDelete() {
        model.deleteFilter({
            filterId: filter.id,
            isGlobal: isGlobal,
            stageId: stageId,
        }).catch(() => boardContext.toastError(model.apiCallError));
    }

    const counts = model.affectedByFilterId[filter.id];
    const countsSection = <div className="update_card_counts">
        <TargetCounts
            status={model.affectedStatus}
            numDevices={counts?.devicesCount}
            numLocations={counts?.locationsCount}
        />
    </div>

    switch (filter.type) {
        case FilterType.Locations:
            filterBody = (<>
                <div className="update_card_title">
                    <FontAwesomeIcon icon="crosshairs" className="update_card_titleIcon" />
                    Locations
                </div>
                <div className="update_filterDescription">
                    Must be at one of <b>{filter.locations.length}</b> locations.
                </div>
                {countsSection}
            </>);
            break;
        case FilterType.Serials:
            filterBody = (<>
                <div className="update_card_title">
                    <FontAwesomeIcon icon="hdd" className="update_card_titleIcon" />
                    Serial Numbers
                </div>
                <div className="update_filterDescription">
                    Must be one of <b>{filter.serialNumbers.length}</b> serial numbers.
                </div>
                {countsSection}
            </>);
            break;
        case FilterType.ThingIDs:
            filterBody = (<>
                <div className="update_card_title">
                    <FontAwesomeIcon icon="hdd" className="update_card_titleIcon" />
                    ThingIDs
                </div>
                <div className="update_filterDescription">
                    Must be one of <b>{filter.thingIDs.length}</b> thingIDs
                </div>
                {countsSection}
            </>);
            break;
        case FilterType.DevicesPerLocation:
            filterBody = (<>
                <div className="update_card_title">
                    <FontAwesomeIcon icon="filter" className="update_card_titleIcon" />
                    Devices per Location
                </div>
                <div className="update_filterDescription">
                    <div>
                        Location must have at least <b>{filter.minNumberOfDevicesPerLocation}</b> {pluralize(filter.minNumberOfDevicesPerLocation, 'device', 'devices')}.
                    </div>
                    <div>
                        Deploy to <b>{filter.numberOfDevicesPerLocationToDeploy}</b> {pluralize(filter.numberOfDevicesPerLocationToDeploy, 'device', 'devices')} per location.
                    </div>
                </div>
                {countsSection}
            </>);
            break;
        case FilterType.Percentage:
            filterBody = (<>
                <div className="update_card_title ellipsis">
                    {filter.percentageOfDevices}% of Total
                </div>
                <div className="update_filterDescription" style={{marginBottom: '0'}}>
                    Deploys to a percentage of the total devices targeted by {isGlobal ? 'global filters' : 'this stage'}.
                </div>
            </>);
            break;
        case FilterType.MenuVersion:
            filterBody = (<>
                <div className="update_card_title ellipsis">
                    Menu Version
                </div>
                <div className="update_filterDescription" >
                    <div><b>Must have the menu version:</b></div>
                    {filter.menuKey}: {filter.menuValue}
                </div>
                {countsSection}
            </>);
            break;
        case FilterType.FirmwareVersion:
            filterBody = (<>
                <div className="update_card_title ellipsis">
                    Firmware Version
                </div>
                <div className="update_filterDescription" >
                    <div><b>Must have the firmware version:</b></div>
                    {filter.firmwareKey}: {filter.firmwareValue}
                </div>
                {countsSection}
            </>);
            break;
        case FilterType.LocationData:
            filterBody = (<>
                <div className="update_card_title ellipsis">
                    <FontAwesomeIcon icon="search-location" className="update_card_titleIcon" />

                    Location Attribute
                </div>
                <div className="update_filterDescription">
                    <div><b>{t(`locationAttr.${filter.locationAttr}`)}:</b></div>
                    {(() => {
                        let valuesToShow = filter.locationAttrValues || [];
                        let showMoreMessage: ReactNode = null;
                        const max = 5;

                        if (valuesToShow.length > max) {
                            showMoreMessage = <div>
                                {`... and ${valuesToShow.length - max} more.`}
                            </div>;
                            valuesToShow = valuesToShow.slice(0, max);
                        }

                        return <>
                            {valuesToShow.map((val, i) => (
                                <div key={i} className="ellipsis">
                                    {val}
                                </div>
                            ))}
                            {showMoreMessage}
                        </>
                    })()}
                </div>
                {countsSection}
            </>);
            break;
        case FilterType.LastSeen:
            filterBody = (<>
                <div className="update_card_title ellipsis">
                    Last Seen Date
                </div>
                <div className="update_filterDescription">
                    {filter.lastSeenOperator === '>' ? 'After' : 'Before'}&nbsp;
                    {formatDateAndTime(filter.lastSeenTime, timezone, hasTimezone)}
                </div>
                {countsSection}
            </>);
            break;
        default:
            filterBody = 'Unknown filterType: ' + filter.type;
            break;
    }

    return (<div
        className={cn('update_card',  'clickable', 'update_filter', {
            isDragging: dragSnapshot.isDragging,
        })}
        onClick={onClick}
        {...dragProvided.dragHandleProps}
    >
        {filterBody}
        {counts?.err && (
            <div className="update_card_warnings error">
                <div className="update_card_warningsIconWrap">
                    <FontAwesomeIcon icon="times" />
                </div>
                <div className="update_card_warningsText">
                    {counts.err}
                </div>
            </div>
        )}
        <FilterMenu
            canEdit={canEdit}
            className="update_cardMenu"
            filter={filter}
            isGlobalFilters={isGlobal}
            isNew={false}
            menuRef={menuRef}
            model={model}
            stageIndex={stageIndex}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
        />
    </div>);
}
